import distinctColors from "distinct-colors";
import { FeatureCollection, Geometry } from "geojson";
import React from "react";
import { FileResponse, useProjectApi } from "~/api/GeneratedApi";
import { staleTime30mins, useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { useDateFormatter } from "~/utils/hooks/useDateFormatter";

type UseProjectProgessprops = {
  id: number | undefined;
};

type GeoJsonExtraTypes = {
  name: string;
};

export type CheckBoxItems = undefined | string[];

export type UseShapeFileTypes = {
  progressJson: GeoJsonType<GeojsonAttributes> | null | undefined;
  vessels: CheckBoxItems;
  progressAreas: CheckBoxItems;
  planJson: GeoJsonType<GeojsonAttributes> | null | undefined;
  hasPlan: boolean;
  sliderMarks:
    | {
        value: number;
        label: string;
        hoverText: string;
      }[]
    | undefined;
  sliderMax: number;
  mapItemColors: Partial<
    Record<keyof GeojsonAttributes, Record<string, chroma.Color>>
  >;
  hasProgress: boolean;
  progressLoading: boolean;
  planAreas: string[];
};

type GeoJsonType<T> = FeatureCollection<Geometry & { name: string }, T> &
  GeoJsonExtraTypes;

export function blobToJson<T>(blob: Blob): Promise<GeoJsonType<T>> {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      if (typeof e.target?.result === "string")
        resolve(JSON.parse(e.target?.result) as Promise<GeoJsonType<T>>);
    };
    reader.readAsText(blob);
  });
}

export type ColorPaletteObject = Partial<
  Record<keyof GeojsonAttributes, Record<string, chroma.Color>>
>;

const sortByDate = (d: GeoJsonType<GeojsonAttributes> | null) => {
  if (d === null) return d;
  return {
    ...d,
    features: d.features.sort(
      (a, b) => Date.parse(a.properties.Date) - Date.parse(b.properties.Date),
    ),
  };
};

export const useProjectShapeFile = ({
  id,
}: UseProjectProgessprops): UseShapeFileTypes => {
  const { getGeoJson, getGeoJson2 } = useProjectApi();
  const formatDate = useDateFormatter();

  const { data: progressJson, isLoading: progressIsLoading } = useCustomQuery<
    GeoJsonType<GeojsonAttributes> | null,
    unknown
  >(
    { key: "shape-progress", id },
    async () => {
      const res = (await getGeoJson(id as number)) as FileResponse | null;
      if (res === null) return null;
      return blobToJson<GeojsonAttributes>(res.data);
    },

    {
      enabled: id !== undefined,
      disableErrorNoti: true,
      staleTime: staleTime30mins,
      select: sortByDate,
    },
  );

  const { data: planJson } = useCustomQuery<
    GeoJsonType<GeojsonAttributes> | null,
    unknown
  >(
    { key: "shape-plan", id },
    async () => {
      const res = (await getGeoJson2(id as number)) as FileResponse | null;
      if (res === null) return null;
      return blobToJson<GeojsonAttributes>(res.data);
    },

    {
      enabled: id !== undefined,
      disableErrorNoti: true,
      staleTime: staleTime30mins,
    },
  );

  // const progressJson = React.useMemo(
  //   () => progressJson ?? { features: [], type: "FeatureCollection", name: "" },
  //   [progressJson],
  // ) as FeatureCollection<MultiPolygon, ProgressProperties> & {
  //   name: string;
  // };

  const sliderMarks = React.useMemo(() => {
    return progressJson?.features.map((f, index) => ({
      value: index,
      label: "",
      // index === 0 || index === geoJson.features.length - 1
      //   ? f.properties.FullName
      //   : "",
      hoverText: formatDate({
        date: Date.parse(f.properties.Date),
        format: "dd-MM-yyyy",
      }),
      // label: formatDate({
      //   date: Date.parse(f.properties.date),
      //   format: "dd-MM-yyyy",
      // }),
    }));
  }, [formatDate, progressJson?.features]);

  const hasPlan = React.useMemo(() => !!planJson, [planJson]);
  const hasProgress = React.useMemo(() => !!progressJson, [progressJson]);

  const sliderMax = React.useMemo(() => {
    if (progressJson?.features.length) {
      return progressJson.features.length - 1;
    }
    return 100;
  }, [progressJson?.features.length]);

  const progressVessels = React.useMemo(
    () =>
      progressJson?.features.reduce<string[]>((tot, curr) => {
        if (!tot.includes(curr?.properties?.Vessel))
          return [...tot, curr?.properties?.Vessel];

        return [...tot];
      }, []),
    [progressJson?.features],
  );

  const progressAreas = React.useMemo(
    () =>
      progressJson?.features
        .reduce<string[]>((tot, curr) => {
          if (
            !tot.includes(curr?.properties?.SubArea) &&
            curr?.properties.SubArea !== undefined
          ) {
            return [...tot, curr?.properties?.SubArea];
          }

          return [...tot];
        }, [])
        .sort(),
    [progressJson?.features],
  );

  const planAreas = React.useMemo(
    () =>
      planJson?.features
        .reduce<string[]>((tot, curr) => {
          if (
            !tot.includes(curr?.properties?.SubArea) &&
            curr?.properties?.SubArea !== undefined
          )
            return [...tot, curr?.properties?.SubArea];

          return [...tot];
        }, [])
        .sort() ?? [],
    [planJson?.features],
  );

  // luodaan väri taulukot areaoille ja vesseleille

  const vesselColors = React.useMemo(
    () => distinctColors({ count: progressVessels?.length, lightMin: 45 }),
    [progressVessels?.length],
  );

  const areaColors = React.useMemo(
    () => distinctColors({ count: progressAreas?.length, lightMin: 45 }),
    [progressAreas?.length],
  );

  const mapItemColors: ColorPaletteObject = React.useMemo(() => {
    const colorObject: ColorPaletteObject = { SubArea: {}, Vessel: {} };

    // Luodaan objecti jonka key on arean nimi ja valuena väri

    progressAreas?.forEach((a, i) => {
      if (colorObject?.SubArea !== undefined && a !== undefined)
        colorObject.SubArea[a] = areaColors[i];
    });

    progressVessels?.forEach((v, i) => {
      if (colorObject?.Vessel !== undefined && v !== undefined)
        colorObject.Vessel[v] = vesselColors[i];
    });

    return colorObject;
  }, [areaColors, progressAreas, progressVessels, vesselColors]);

  return {
    progressJson,
    vessels: progressVessels,
    progressAreas,
    planJson,
    hasPlan,
    hasProgress,
    sliderMarks,
    sliderMax,
    mapItemColors,
    progressLoading: progressIsLoading,
    planAreas,
  };
};

// export type PropertiesProps = {
//   Product: string;
//   Area: string;
//   FairwayD: number;
//   FwayID: number;
//   Area__km2_: number;
//   CellName: string;
//   Subarea: string;
//   Vessel: string;
//   Prod: string;
//   Haraussyvy: number;
//   FullName: string;
// };

// export type GeojsonAttributes = {
//   Cell_ID: string;

//   gdo_gid: number;
//   id: number;
// };
export type GeojsonAttributes = {
  SubArea: string;
  Vessel: string;
  Date: string;
  GeomArea: number;
  GeomLine: number;
  hours: number;
  Project: string;
};

// export type ProgressProperties = {
//   Date: string;
//   Project: string;
//   SubArea: string;
//   Product: string;
//   Method: string;
//   Vessel: string;
//   Surveyor: string;
//   Remarks: null;
//   Hours: number;
//   LineKM: number;
//   Area: number;
//   Status: string;
// };
